import { useEffect, useRef } from 'react';

/**
 * useBuildRefresh — detects that a newer build has been deployed while this
 * tab was open. It NEVER reloads on its own: a silent reload destroys
 * anything the user was typing. It raises `staffed:newbuild`, and
 * BuildUpdateBar offers the user a Refresh button on their own terms.
 */
const HASH_RE = /\/static\/js\/main\.([A-Za-z0-9]+)\.js/;

function currentHash() {
  const el = document.querySelector('script[src*="/static/js/main."]');
  const m = el && el.src.match(HASH_RE);
  return m ? m[1] : null;
}

export default function useBuildRefresh() {
  const bootHash = useRef(null);
  const lastCheck = useRef(0);
  const announced = useRef(false);

  useEffect(() => {
    bootHash.current = currentHash();
    // No hash → not a hashed production build (e.g. local dev). Nothing to do.
    if (!bootHash.current) return;

    const check = async () => {
      if (announced.current) return;
      const now = Date.now();
      if (now - lastCheck.current < 60000) return; // throttle to once/min
      lastCheck.current = now;
      try {
        const res = await fetch(`/index.html?_=${now}`, { cache: 'no-store' });
        if (!res.ok) return;
        const html = await res.text();
        const m = html.match(HASH_RE);
        const latest = m ? m[1] : null;
        if (latest) window.__staffedLatestBuildHash = latest;
        if (latest && bootHash.current && latest !== bootHash.current) {
          announced.current = true;
          window.__staffedNewBuild = true;
          window.dispatchEvent(new CustomEvent('staffed:newbuild'));
        }
      } catch {
        /* offline or transient — retry on next focus */
      }
    };

    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    const id = setInterval(() => { if (document.visibilityState === 'visible') check(); }, 5 * 60 * 1000);
    const t = setTimeout(check, 3000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      clearInterval(id);
      clearTimeout(t);
    };
  }, []);
}
